/**
 * src/modules/common/hooks/useElementHeights.ts
 *
 * Hook to calculate the total height of an array of elements
 *
 * @copyright DesignShop, 2024
 * @since 09/19/2024
 */
import { useEffect, useState } from 'react';

export function useElementHeights(elementIds: string[]) {
  const [totalHeight, setTotalHeight] = useState(0);

  useEffect(() => {
    const calculateTotalHeight = () => {
      // `getBoundingClientRect().height` returns a sub-pixel-precise float,
      // unlike `offsetHeight` which is rounded to an integer. Using the
      // rounded value to position sticky elements stacked below another
      // sticky element causes a 1px gap on devices/setups where the source
      // element renders at a fractional height (borders, shadows, fractional
      // padding, non-integer device pixel ratio).
      const newTotalHeight = elementIds.reduce((sum, id) => {
        const element = document.getElementById(id);
        return sum + (element?.getBoundingClientRect().height ?? 0);
      }, 0);

      setTotalHeight((prev) => (prev !== newTotalHeight ? newTotalHeight : prev));
    };

    calculateTotalHeight();

    // Add resize event listener to recalculate heights on window resize
    window.addEventListener('resize', calculateTotalHeight);

    // Clean up the event listener on component unmount
    return () => {
      window.removeEventListener('resize', calculateTotalHeight);
    };
  }, [elementIds]);

  return totalHeight;
}

export default useElementHeights;
